<template>
  <div class="hls-player-wrapper">
    <video
      v-if="isHlsSupported"
      ref="hlsPlayer"
      controls
      :poster="poster"
      @loadedmetadata="$event => $emit('loadedmetadata', $event)"
    ></video>
    <div v-else>
      <GoldcastText color="rgba(255, 0, 0, 0.7)" :font-size="18" textAlign="left" lineHeightCustom="40px"
        >Sorry, your browser doesn't support hls player.</GoldcastText
      >
    </div>
  </div>
</template>

<script>
import logger from '@goldcast/logger';
import Hls from 'hls.js';
import { mapGetters } from 'vuex';

import { showErrorNotification } from '@/utils/utils.js';

export default {
  name: 'HlsPlayer',
  props: {
    videoStreamUrl: {
      type: String,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    poster: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      hlsInstance: null,
      isHlsSupported: true
    };
  },
  mounted() {
    this.isHlsSupported = Hls.isSupported();
    this.initPlayer();
  },
  beforeUnmount() {
    this.cleanupPlayer();
  },
  computed: {
    ...mapGetters('org', { org: 'getCurrentOrg' }),
    ...mapGetters('user', { getJwtAccessToken: 'getJwtAccessToken' })
  },
  watch: {
    videoStreamUrl: {
      handler(newUrl, oldUrl) {
        if (newUrl !== oldUrl) {
          this.cleanupPlayer();
          this.initPlayer();
        }
      },
      immediate: true
    }
  },
  methods: {
    cleanupPlayer() {
      if (this.hlsInstance) {
        // Remove event listeners first
        this.hlsInstance.off(Hls.Events.ERROR, this.handleHlsErrors);
        this.hlsInstance.off(Hls.Events.MANIFEST_PARSED, this.handleManifestParsed);
        this.hlsInstance.off(Hls.Events.MEDIA_ATTACHED, this.handleMediaAttached);

        // Destroy instance
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },
    initPlayer() {
      if (this.isHlsSupported) {
        this.hlsInstance = new Hls({
          xhrSetup: (xhr, url) => {
            if (
              url.includes('.m3u8') &&
              this.org.useHLSGuard &&
              (url.includes('mediastore') || url.includes('simulive.goldcast') || url.includes('vid.goldcast'))
            ) {
              const token = this.getJwtAccessToken || '';
              const separator = url.includes('?') ? '&' : '?';
              let finalUrl = `${url}${separator}org_id=${this.org.id}`;
              if (token) {
                finalUrl += `&code=${encodeURIComponent(token)}`;
              }
              xhr.open('GET', finalUrl, true);
            }
          }
        });
        const video = this.$refs.hlsPlayer;
        this.hlsInstance.attachMedia(video);

        this.hlsInstance.on(Hls.Events.MEDIA_ATTACHED, this.handleMediaAttached);
        this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, this.handleManifestParsed);
        this.hlsInstance.on(Hls.Events.ERROR, this.handleHlsErrors);
      } else {
        logger.error('[HlsPlayer] Browser does not support HLS player');
      }
    },
    handleMediaAttached() {
      this.hlsInstance.loadSource(this.videoStreamUrl);
    },
    handleManifestParsed() {
      const video = this.$refs.hlsPlayer;
      if (this.autoplay) {
        this.handleAutoplay(video);
      } else {
        video.pause();
      }
    },
    handleAutoplay(video) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          logger.warn('[HlsPlayer] Autoplay prevented by browser policy:', error);
        });
      }
    },
    handleHlsErrors(event, errorDetails) {
      logger.warn('[HlsPlayer] HLS Error:', event, errorDetails);

      if (errorDetails.fatal) {
        switch (errorDetails.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            logger.warn('[HlsPlayer] Fatal network error, trying to recover...');
            this.hlsInstance.startLoad();
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            logger.warn('[HlsPlayer] Fatal media error, trying to recover...');
            this.hlsInstance.recoverMediaError();
            break;

          default:
            logger.error('[HlsPlayer] Fatal error, cannot recover');
            this.cleanupPlayer();
            showErrorNotification('Video playback failed. Please refresh the page.', 5000);
            break;
        }
      } else {
        // Non-fatal errors - log but don't interrupt playback
        if (!['bufferStalledError', 'bufferNudgeOnStall'].includes(errorDetails.details)) {
          logger.warn('[HlsPlayer] Non-fatal HLS error:', errorDetails.details);
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.hls-player-wrapper {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  video {
    width: 100%;
    height: auto;
    max-height: 600px;
  }
}
</style>
